import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { IPrinterDefaultFormValues, IPrinterFormValues, printerFormSchema } from '../../../../@types/model/masterData/printer/printerFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IPrinterFormProps {
    isLoading : boolean;
    initialValues : IPrinterFormValues | IPrinterDefaultFormValues;
    onSubmit : (values : IPrinterFormValues) => void;
    onClose : () => void;
}

const PrinterForm = (props : IPrinterFormProps) : React.ReactElement => {
    const schema = printerFormSchema;
    const { initialValues, onSubmit, onClose, isLoading } = props;

    const { handleSubmit, control, formState: { isValid, isDirty } } = useForm<IPrinterFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const printServers = useAppSelector(state => state.masterData.printServers);
    const printServerOptions : Array<IOptionType> = useMemo(() => {
        return printServers?.filter(x => x.isActive).map(x => ({ label: `${x.name}`, value: x.id })) ?? [];
    }, [printServers]);

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)} className={'p20'}>
                <div className={'fdc aic'}>
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={initialValues.name} />
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'defaultPaper'}
                        label={'Default Paper'}
                        control={control}
                        defaultValue={initialValues.defaultPaper} />
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'ipAddress'}
                        label={'IP Address'}
                        control={control}
                        defaultValue={initialValues.ipAddress} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'printServer'}
                        label={'Print Server'}
                        control={control}
                        options={printServerOptions}
                        defaultValues={initialValues.printServer ?? null} />
                    <div className={'fdr jcfs mb5'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={initialValues.isActive} />
                    </div>
                    <div className={'fdr ml10 ais jcfe pt10 pb10'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'} />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PrinterForm;
